import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { ArrowLeft, ChevronDown, Mail, Leaf, Search } from "lucide-react";
import { FAQ_FLAT, FAQ_SECTIONS } from "../data/faq";

const SCHEMA_ID = "faq-jsonld-schema";

function useFaqJsonLd() {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_FLAT.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    };
    let el = document.getElementById(SCHEMA_ID);
    if (!el) {
      el = document.createElement("script");
      el.id = SCHEMA_ID;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
    return () => {
      const node = document.getElementById(SCHEMA_ID);
      if (node) node.remove();
    };
  }, []);
}

export default function AboutScreen({ type = "about" }) {
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState(null);
  const [query, setQuery] = useState("");

  // Inject JSON-LD FAQPage schema only when viewing About/FAQ
  useFaqJsonLd();

  const titleMap = { about: "About & Help", privacy: "Privacy Policy", terms: "Terms of Service" };

  // Filtered sections (real-time search across question + answer text)
  const visibleSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_SECTIONS;
    return FAQ_SECTIONS.map((s) => ({
      ...s,
      items: s.items.filter(
        (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
      ),
    })).filter((s) => s.items.length > 0);
  }, [query]);

  return (
    <Shell section="legal">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline" data-testid="legal-back">
        <ArrowLeft size={14} /> Back
      </button>

      <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-shadow-green">{titleMap[type]}</h1>
      <p className="text-muted-soft text-xs uppercase tracking-wider mb-6">v1.0 · Updated May 2026</p>

      {type === "about" && (
        <>
          <div className="glass-card-strong p-6 mb-5 fade-up">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full"
                    style={{ background: "rgba(76,175,80,0.18)", boxShadow: "var(--glow-green)" }}>
                <Leaf size={20} className="text-[#4CAF50]" />
              </span>
              <h2 className="text-lg font-bold">Our Mission</h2>
            </div>
            <p className="text-soft text-sm leading-relaxed">
              Care for Flora exists to make plant care accessible to everyone. Identify any of 400,000+ species,
              diagnose diseases instantly, and grow with personalised AI care guidance — at home, in your garden,
              or wherever life takes you.
            </p>
          </div>

          <h2 className="text-base font-semibold uppercase tracking-wider text-soft mb-3">Frequently Asked Questions</h2>

          {/* Search bar */}
          <div className="relative mb-5 fade-up" data-testid="faq-search-wrap">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4CAF50] opacity-70" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the FAQ…"
              className="glass-input w-full pl-9"
              data-testid="faq-search-input"
            />
          </div>

          {visibleSections.length === 0 && (
            <p className="text-soft text-sm py-6 text-center" data-testid="faq-no-results">
              No questions match “{query}”. Try a different keyword.
            </p>
          )}

          {visibleSections.map((section, sIdx) => (
            <div key={section.title} className="mb-7" data-testid={`faq-section-${sIdx}`}>
              <h3
                className="text-[11px] font-bold uppercase tracking-[0.16em] mb-3 pb-1.5"
                style={{
                  color: "#4CAF50",
                  borderBottom: "1px solid rgba(76,175,80,0.25)",
                }}
              >
                {section.title}
              </h3>
              <div className="space-y-2">
                {section.items.map((f, i) => {
                  const key = `${sIdx}-${i}`;
                  const open = openKey === key;
                  return (
                    <div key={key} className="glass-card overflow-hidden fade-up" data-testid={`faq-item-${sIdx}-${i}`}>
                      <button
                        onClick={() => setOpenKey(open ? null : key)}
                        className="w-full flex items-center justify-between p-4 text-left gap-3"
                        aria-expanded={open}
                      >
                        <span className="text-sm font-semibold flex-1">{f.q}</span>
                        <ChevronDown
                          size={16}
                          className={`text-[#4CAF50] transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
                        />
                      </button>
                      {open && (
                        <div
                          className="px-4 pb-4 text-sm text-soft border-t border-white/5 pt-3 leading-relaxed whitespace-pre-line"
                          data-testid={`faq-answer-${sIdx}-${i}`}
                        >
                          {f.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            onClick={() => (window.location.href = "mailto:support@careforflora.app")}
            className="btn-outline w-full inline-flex items-center justify-center gap-2"
            data-testid="faq-contact-support"
          >
            <Mail size={16} /> Email Support
          </button>
        </>
      )}

      {type === "privacy" && (
        <div className="glass-card-strong p-6 text-sm text-soft leading-relaxed fade-up space-y-4">
          <SectionHeading>1. Data we collect</SectionHeading>
          <p>We collect your email, name, country, and plant photos you submit for identification.</p>
          <SectionHeading>2. How we use it</SectionHeading>
          <p>Solely for plant identification, disease detection and personalised care. Photos are NEVER used for facial recognition.</p>
          <SectionHeading>3. Retention</SectionHeading>
          <p>Identification scans are stored until you delete them. Platinum journal entries auto-delete after 3 days. Deleting your account erases all data within 30 days.</p>
          <SectionHeading>4. Your rights (GDPR/CCPA)</SectionHeading>
          <p>You can export, edit, or delete your data anytime via Profile → Settings.</p>
        </div>
      )}

      {type === "terms" && (
        <div className="glass-card-strong p-6 text-sm text-soft leading-relaxed fade-up space-y-4">
          <SectionHeading>1. Acceptable use</SectionHeading>
          <p>You must be 13+ to use Care for Flora. Don't upload illegal or harmful content.</p>
          <SectionHeading>2. AI advice disclaimer</SectionHeading>
          <p>AI suggestions are informational. For severe pest infestations or rare plants, consult a professional horticulturist.</p>
          <SectionHeading>3. Subscriptions</SectionHeading>
          <p>Premium and Platinum auto-renew unless cancelled. Cancel anytime in Profile → Subscription.</p>
          <SectionHeading>4. Liability</SectionHeading>
          <p>Care for Flora is provided "as is" without warranty. We are not responsible for plant outcomes resulting from app advice.</p>
        </div>
      )}
    </Shell>
  );
}

function SectionHeading({ children }) {
  return (
    <h3
      className="text-white font-semibold pb-2"
      style={{
        fontSize: 18,
        borderBottom: "1px solid rgba(76,175,80,0.4)",
        boxShadow: "0 1px 8px rgba(76,175,80,0.25)",
      }}
    >
      {children}
    </h3>
  );
}
